/**
 * Parser de markdown mínimo para as respostas do assistente.
 *
 * SEGURANÇA: o texto vem de um LLM (e, indiretamente, de cadastros do cliente),
 * então NUNCA vira HTML. Este módulo só produz uma árvore de dados
 * (parágrafos, listas, tabelas, tokens inline); quem renderiza usa elementos
 * React/MUI — o React escapa todo texto. Sem `dangerouslySetInnerHTML`.
 * Suporta só o necessário: parágrafos, títulos (viram negrito), listas
 * (- * 1.), tabelas com pipe, **negrito**, `código` e [rótulo](/rota-interna).
 * HTML cru, imagens e links externos não são interpretados (ficam como texto).
 */

const RE_INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\[[^\]\n]+\]\([^)\s]+\))/g;
const RE_SEPARADOR_TABELA = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const RE_NUMERICO = /^[-+]?\s*(R\$\s*)?-?\d[\d.]*(,\d+)?\s*(%|un\.?)?$/;

/** Quebra uma linha em tokens inline: text | bold | code | link. */
export function parseInline(texto) {
  const tokens = [];
  let ultimo = 0;
  let m;
  RE_INLINE.lastIndex = 0;
  while ((m = RE_INLINE.exec(texto)) !== null) {
    if (m.index > ultimo) tokens.push({ t: 'text', v: texto.slice(ultimo, m.index) });
    const bruto = m[0];
    if (bruto.startsWith('**')) {
      tokens.push({ t: 'bold', v: bruto.slice(2, -2) });
    } else if (bruto.startsWith('`')) {
      tokens.push({ t: 'code', v: bruto.slice(1, -1) });
    } else {
      const meio = bruto.indexOf('](');
      tokens.push({ t: 'link', label: bruto.slice(1, meio), href: bruto.slice(meio + 2, -1) });
    }
    ultimo = m.index + bruto.length;
  }
  if (ultimo < texto.length) tokens.push({ t: 'text', v: texto.slice(ultimo) });
  return tokens;
}

function dividirLinhaTabela(linha) {
  let s = linha.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  // Divide em "|" respeitando "\|" escapado (sem lookbehind: Safari antigo).
  const celulas = [];
  let atual = '';
  for (let k = 0; k < s.length; k += 1) {
    if (s[k] === '\\' && s[k + 1] === '|') {
      atual += '|';
      k += 1;
    } else if (s[k] === '|') {
      celulas.push(atual.trim());
      atual = '';
    } else {
      atual += s[k];
    }
  }
  celulas.push(atual.trim());
  return celulas;
}

function alinhamentos(linhaSeparador) {
  return dividirLinhaTabela(linhaSeparador).map((c) => {
    const e = c.startsWith(':');
    const d = c.endsWith(':');
    if (e && d) return 'center';
    if (d) return 'right';
    return 'left';
  });
}

/** Célula "numérica" (R$ 1.234,50 / 12 / 12,4%) → alinhada à direita. */
export function ehCelulaNumerica(texto) {
  return RE_NUMERICO.test(String(texto).replace(/\*\*/g, '').trim());
}

const ehItemLista = (l) => /^\s*([-*•]|\d+[.)])\s+/.test(l);
const ehOrdenada = (l) => /^\s*\d+[.)]\s+/.test(l);
const semMarcador = (l) => l.replace(/^\s*([-*•]|\d+[.)])\s+/, '');
const pareceLinhaTabela = (l) => l.includes('|') && l.trim().length > 1;

/**
 * Converte markdown em blocos:
 *  {type:'p'|'h', inlines}  {type:'ul'|'ol', items:[inlines]}
 *  {type:'table', head:[inlines], aligns:[], rows:[[inlines]], numeric:[bool]}
 */
export function parseMarkdown(fonte) {
  const linhas = String(fonte || '').replace(/\r\n/g, '\n').split('\n');
  const blocos = [];
  let paragrafo = [];

  const fecharParagrafo = () => {
    if (paragrafo.length) {
      blocos.push({ type: 'p', inlines: parseInline(paragrafo.join(' ')) });
      paragrafo = [];
    }
  };

  for (let i = 0; i < linhas.length; i += 1) {
    const linha = linhas[i];

    if (linha.trim() === '') {
      fecharParagrafo();
      continue;
    }

    // Tabela: linha com pipes seguida da linha separadora (---|---).
    if (pareceLinhaTabela(linha) && i + 1 < linhas.length && RE_SEPARADOR_TABELA.test(linhas[i + 1]) && linhas[i + 1].includes('-')) {
      fecharParagrafo();
      const head = dividirLinhaTabela(linha);
      const aligns = alinhamentos(linhas[i + 1]);
      const rows = [];
      i += 2;
      while (i < linhas.length && linhas[i].trim() !== '' && pareceLinhaTabela(linhas[i])) {
        rows.push(dividirLinhaTabela(linhas[i]));
        i += 1;
      }
      i -= 1;
      const colunas = head.length;
      const numeric = head.map((_, c) => rows.length > 0 && rows.every((r) => (r[c] === undefined || r[c] === '' ? true : ehCelulaNumerica(r[c]))));
      blocos.push({
        type: 'table',
        head: head.map(parseInline),
        aligns,
        numeric,
        rows: rows.map((r) => Array.from({ length: colunas }, (_, c) => parseInline(r[c] ?? ''))),
      });
      continue;
    }

    // Título: vira parágrafo em negrito (sem h1 gigante dentro do balão).
    const titulo = /^\s{0,3}#{1,6}\s+(.*)$/.exec(linha);
    if (titulo) {
      fecharParagrafo();
      blocos.push({ type: 'h', inlines: parseInline(titulo[1]) });
      continue;
    }

    // Lista.
    if (ehItemLista(linha)) {
      fecharParagrafo();
      const ordenada = ehOrdenada(linha);
      const items = [];
      while (i < linhas.length && ehItemLista(linhas[i])) {
        items.push(parseInline(semMarcador(linhas[i])));
        i += 1;
      }
      i -= 1;
      blocos.push({ type: ordenada ? 'ol' : 'ul', items });
      continue;
    }

    paragrafo.push(linha.trim());
  }
  fecharParagrafo();
  return blocos;
}

/** Texto puro de tokens inline (para copiar resposta, aria-label etc.). */
export function inlinesParaTexto(inlines) {
  return inlines.map((t) => (t.t === 'link' ? t.label : t.v)).join('');
}
